<template>
  <div class="crypto-first-step">
    <div class="search-wrapper crypto-first-step__search w-100">
      <input
        ref="searchInput"
        type="text"
        v-model="searchData"
        class="search-input"
        placeholder="Search currencies"
        autocomplete="off"
      />
      <span
        v-if="searchData !== ''"
        class="clear-data"
        role="button"
        tabindex="0"
        @click="clearSearchData"
        @keydown.enter.prevent="clearSearchData"
      >
        <img src="@/assets/img/close.svg" alt="" />
      </span>
    </div>

    <div
      v-if="!walletOptions || !walletOptions.length"
      class="crypto-first-step__empty"
    >
      No currencies match your search.
    </div>

    <div v-else class="currencies-list crypto-first-step__list" role="list">
      <button
        v-for="(wallet, i) in walletOptions"
        :key="`${wallet.currency}-${i}`"
        type="button"
        role="listitem"
        :class="[
          'currency-card',
          wallet.tags && wallet.tags.includes('promoted')
            ? 'currency-card--promoted'
            : '',
        ]"
        @click="exchangeCoinChange(wallet)"
      >
        <div class="currency-card__main">
          <div class="currency-card__icon-wrap">
            <img
              :src="wallet.logo"
              :alt="wallet.currency"
              class="currency-card__icon"
              :id="'currency-icon' + i"
            />
            <b-tooltip
              :target="'currency-icon' + i"
              placement="top"
              class="my-tooltip-class text-capitalize"
            >
              <span class="text-capitalize">
                Network: {{ wallet.chainsNetworkFullTitle }}
              </span>
            </b-tooltip>
          </div>

          <div class="currency-card__copy">
            <span class="currency-card__name">{{
              currencyDisplayName(wallet)
            }}</span>
            <span class="currency-card__meta-line">
              <span class="currency-card__meta">{{
                currencyNetworkLabel(wallet)
              }}</span>
              <span
                v-if="wallet && wallet.tags && wallet.tags.includes('promoted')"
                class="currency-card__promoted"
              >
                <img
                  src="@/assets/img/poweredby.svg"
                  alt="Powered by Concordium"
                />
              </span>
            </span>
          </div>
        </div>

        <div class="currency-card__aside">
          <span
            v-if="wallet && wallet.fasterConfirmation"
            class="currency-card__badge"
          >
            <img src="@/assets/img/lightning.svg" alt="" aria-hidden="true" />
            Fast
          </span>
          <span class="currency-card__chevron" aria-hidden="true">›</span>
        </div>
      </button>
    </div>

    <div class="crypto-first-step__faster faster-payment">
      <img
        class="logo"
        src="@/assets/img/lightning.svg"
        alt=""
        aria-hidden="true"
      />
      <span class="crypto-first-step__faster-label">Faster Payments</span>
      <span
        v-b-tooltip.hover="fasterPaymentsTooltip"
        class="crypto-first-step__faster-info"
        tabindex="0"
        role="button"
        :aria-label="$t('payment.faster-tooltip')"
      >
        <img
          class="logo-i"
          src="@/assets/img/info_blue.svg"
          alt=""
          aria-hidden="true"
        />
      </span>
    </div>
  </div>
</template>
<script>
/* eslint-disable */
import _ from "lodash";
import { USER_TRACK_EVENTS } from "@/store/modules/user/consts";

export default {
  name: "FirstStep",
  props: {
    walletOptions: Array,
  },
  watch: {
    searchData: _.debounce(function (val) {
      this.$emit("searchData", val);
    }, 500),
  },
  data() {
    return {
      searchData: "",
    };
  },
  computed: {
    fasterPaymentsTooltip() {
      return {
        title: this.$t("payment.faster-tooltip"),
        placement: "left",
        boundary: "window",
        container: "body",
        customClass: "my-tooltip-class tooltip-iframe-safe",
        modifiers: {
          flip: { enabled: false },
          preventOverflow: {
            enabled: true,
            boundariesElement: "viewport",
            padding: 8,
          },
        },
      };
    },
  },
  methods: {
    currencyDisplayName(wallet) {
      const name = ["USDR", "EURR"].includes(wallet.currency)
        ? wallet.name.split("-")[0]
        : wallet.name;
      const code =
        wallet.currency.toUpperCase() === "USDT" ||
        wallet.currency.toUpperCase() === "USDC"
          ? "ERC20"
          : wallet.currency;
      return `${name} (${code})`;
    },
    currencyNetworkLabel(wallet) {
      return wallet.chainsNetworkFullTitle || wallet.currency;
    },
    async clearSearchData() {
      this.searchData = "";
    },
    exchangeCoinChange(wallet) {
      this.$emit("exchangeCoinChange", wallet);

      this.$store.dispatch(`user/${USER_TRACK_EVENTS}`, {
        moduleInfo: {
          current: "Crypto wallet tab",
          activity: "selected cryptocurrency",
          meta: {
            currency: wallet.name,
          },
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.crypto-first-step {
  $radius-sm: 12px;
  $text: #1b1f3b;
  $text-muted: #6e728b;
  $surface: #fff;
  $surface-muted: #f4f6fb;
  $border: rgba(27, 31, 59, 0.08);
  $accent: #5559fe;
  $accent-soft: rgba(85, 89, 254, 0.1);
  $shadow-sm: 0 2px 12px rgba(27, 31, 59, 0.06);

  &__search {
    position: relative;
    margin-bottom: 12px;
  }

  .search-input {
    width: 100%;
    border: 1px solid $border;
    border-radius: $radius-sm;
    padding: 11px 36px 11px 14px;
    font-size: 14px;
    color: $text;
    background: $surface;
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus {
      border-color: rgba(85, 89, 254, 0.4);
      box-shadow: 0 0 0 3px $accent-soft;
    }

    &::placeholder {
      color: $text-muted;
    }
  }

  .search-wrapper {
    position: relative;

    .clear-data {
      position: absolute;
      right: 14px;
      top: 50%;
      transform: translateY(-50%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 6px;
      cursor: pointer;
      line-height: 0;

      img {
        display: block;
        width: 10px;
        height: 10px;
        opacity: 0.55;
      }

      &:hover img {
        opacity: 1;
      }
    }
  }

  &__list {
    --currency-row-height: 72px;
    --currency-row-gap: 8px;
    display: flex;
    flex-direction: column;
    gap: var(--currency-row-gap);
    /* ~2.5 rows visible so users see there is more to scroll */
    max-height: calc(
      var(--currency-row-height) * 2.5 + var(--currency-row-gap) * 2
    );
    overflow-y: auto;
    overflow-x: hidden;
    padding: 2px 2px 4px;
    margin: 0;
    scrollbar-width: thin;
    scrollbar-color: rgba(27, 31, 59, 0.45) rgba(27, 31, 59, 0.08);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: rgba(27, 31, 59, 0.06);
      border-radius: 999px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(27, 31, 59, 0.4);
      border-radius: 999px;

      &:hover {
        background: rgba(27, 31, 59, 0.55);
      }
    }
  }

  &__empty {
    padding: 24px 16px;
    text-align: center;
    font-size: 14px;
    color: $text-muted;
    background: $surface-muted;
    border-radius: $radius-sm;
  }

  &__faster {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    margin-top: 12px;
    cursor: pointer;
  }

  &__faster-label {
    font-size: 12px;
    font-weight: 500;
    color: $text-muted;
  }

  &__faster-info {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    line-height: 0;
    outline: none;

    &:focus-visible {
      outline: 2px solid $accent;
      outline-offset: 2px;
      border-radius: 4px;
    }
  }

  .logo {
    width: 18px;
    height: 18px;
  }

  .logo-i {
    width: 16px;
    height: 16px;
    opacity: 0.7;
  }
}

.currency-card {
  $radius-sm: 12px;
  $text: #1b1f3b;
  $text-muted: #6e728b;
  $surface: #fff;
  $surface-muted: #f4f6fb;
  $border: rgba(27, 31, 59, 0.08);
  $accent: #5559fe;
  $accent-soft: rgba(85, 89, 254, 0.1);
  $shadow-sm: 0 2px 12px rgba(27, 31, 59, 0.06);

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  width: 100%;
  min-height: 50px;
  box-sizing: border-box;
  padding: 2px 12px;
  text-align: left;
  border: 1px solid $border;
  border-radius: $radius-sm;
  background: $surface;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(85, 89, 254, 0.3);
    background: $accent-soft;
    box-shadow: $shadow-sm;
  }

  &:active {
    transform: scale(0.995);
  }

  &:focus-visible {
    outline: 2px solid $accent;
    outline-offset: 2px;
  }

  &--promoted {
    background: linear-gradient(
      135deg,
      rgba(85, 89, 254, 0.06) 0%,
      #f5f6fc 100%
    );
    border-color: rgba(85, 89, 254, 0.18);
  }

  &__main {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  &__icon-wrap {
    flex-shrink: 0;
    width: 44px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background: $surface-muted;
  }

  &__icon {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }

  &__copy {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.3;
    color: $text;
    word-break: break-word;
  }

  &__meta-line {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
  }

  &__meta {
    font-size: 12px;
    font-weight: 400;
    color: $text-muted;
    text-transform: capitalize;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 0 1 auto;
    min-width: 0;
  }

  &__aside {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    color: #b45309;
    background: #fff7ed;
    border-radius: 999px;

    img {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
  }

  &__chevron {
    font-size: 20px;
    font-weight: 300;
    line-height: 1;
    color: $text-muted;
  }

  &__promoted {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    line-height: 0;

    img {
      display: block;
      height: 8px;
      width: auto;
      max-width: 120px;
      object-fit: contain;
      object-position: left center;
    }
  }
}
</style>
